import { z } from "zod";

const AsinSchema = z
  .string()
  .trim()
  .regex(/^[A-Z0-9]{10}$/);
const CalendarDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const AssetIdSchema = z.string().trim().min(1);

export const SponsoredBrandPlacementSchema = z.enum([
  "TOP_OF_SEARCH",
  "HOME",
  "DETAIL_PAGE",
  "OTHER",
]);
export type SponsoredBrandPlacement = z.infer<typeof SponsoredBrandPlacementSchema>;

export const SponsoredBrandSchema = z.object({
  brandId: z.string().min(1),
  brandEntityId: z.string().min(1),
  name: z.string().min(1),
});
export type SponsoredBrand = z.infer<typeof SponsoredBrandSchema>;

export const SponsoredBrandStorePageSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  url: z.string().url(),
  asins: z.array(AsinSchema).default([]),
});

export const SponsoredBrandStoreSchema = z.object({
  entityId: z.string().min(1),
  brandEntityId: z.string().min(1),
  name: z.string().min(1),
  pages: z.array(SponsoredBrandStorePageSchema),
});
export type SponsoredBrandStore = z.infer<typeof SponsoredBrandStoreSchema>;

export const SponsoredBrandAssetSchema = z.object({
  assetId: z.string().min(1),
  version: z.string().min(1),
  name: z.string().min(1),
  type: z.enum(["IMAGE", "VIDEO"]),
  subtypes: z.array(z.string()),
  status: z.string(),
  moderationStatus: z.string().nullable(),
  moderationPolicies: z.record(z.string()),
  approvedPrograms: z.array(z.string()),
  contentType: z.string().nullable(),
  url: z.string().url().nullable(),
  width: z.number().int().nonnegative(),
  height: z.number().int().nonnegative(),
  durationSeconds: z.number().nonnegative(),
  sizeBytes: z.number().int().nonnegative(),
  lastUpdatedAt: z.string().nullable().default(null),
  searchTerms: z.array(z.string()).default([]),
});
export type SponsoredBrandAsset = z.infer<typeof SponsoredBrandAssetSchema>;

export const SponsoredBrandProductEligibilitySchema = z.object({
  asin: AsinSchema,
  status: z.enum(["ELIGIBLE", "ELIGIBLE_WITH_WARNING", "INELIGIBLE"]),
  messages: z.array(z.string()),
});
export type SponsoredBrandProductEligibility = z.infer<
  typeof SponsoredBrandProductEligibilitySchema
>;

export const SponsoredBrandRecommendationSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("keyword"),
    value: z.string().min(1),
    matchType: z.enum(["BROAD", "PHRASE", "EXACT"]),
  }),
  z.object({ type: z.literal("product"), asin: AsinSchema }),
  z.object({
    type: z.literal("category"),
    id: z.string().min(1),
    name: z.string().min(1),
    path: z.string().nullable(),
  }),
]);
export type SponsoredBrandRecommendation = z.infer<typeof SponsoredBrandRecommendationSchema>;

const CropSchema = z.object({
  top: z.number().int().nonnegative(),
  left: z.number().int().nonnegative(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
});

const BrandCreativeSchema = z.object({
  brandName: z.string().trim().min(1).max(30),
  headline: z.string().trim().min(1).max(50),
  brandLogoAssetId: AssetIdSchema,
  brandLogoCrop: CropSchema.default({ top: 0, left: 0, width: 400, height: 400 }),
});

const ProductCollectionAdSchema = z.object({
  format: z.literal("product_collection"),
  name: z.string().trim().min(1),
  products: z.array(AsinSchema).length(3),
  landingPage: z.discriminatedUnion("type", [
    z.object({ type: z.literal("product_list"), asins: z.array(AsinSchema).min(3).max(100) }),
    z.object({ type: z.literal("store"), storeEntityId: z.string().min(1), url: z.string().url() }),
  ]),
  creative: BrandCreativeSchema.extend({
    customImageAssetId: AssetIdSchema,
    customImageCrop: CropSchema,
  }),
});

const VideoAdSchema = z.object({
  format: z.literal("video"),
  name: z.string().trim().min(1),
  products: z.array(AsinSchema).length(1),
  landingPage: z.discriminatedUnion("type", [
    z.object({ type: z.literal("product_detail"), asin: AsinSchema }),
    z.object({ type: z.literal("store"), storeEntityId: z.string().min(1), url: z.string().url() }),
  ]),
  creative: z.object({
    videoAssetId: AssetIdSchema,
    brand: BrandCreativeSchema.nullable(),
  }),
});

const StoreSpotlightAdSchema = z.object({
  format: z.literal("store_spotlight"),
  name: z.string().trim().min(1),
  landingPage: z.object({
    type: z.literal("store"),
    storeEntityId: z.string().min(1),
    url: z.string().url(),
  }),
  creative: BrandCreativeSchema.extend({
    subpages: z
      .array(
        z.object({
          pageId: z.string().min(1),
          pageTitle: z.string().min(1),
          asin: AsinSchema,
          url: z.string().url(),
        }),
      )
      .length(3),
  }),
});

export const SponsoredBrandAdSchema = z.discriminatedUnion("format", [
  ProductCollectionAdSchema,
  VideoAdSchema,
  StoreSpotlightAdSchema,
]);
export type SponsoredBrandAd = z.infer<typeof SponsoredBrandAdSchema>;

const KeywordTargetingSchema = z.object({
  type: z.literal("keyword"),
  targets: z
    .array(
      z.object({
        text: z.string().trim().min(1),
        matchType: z.enum(["BROAD", "PHRASE", "EXACT"]),
        bid: z.number().positive(),
      }),
    )
    .min(1)
    .max(1_000),
  negatives: z
    .array(
      z.object({
        text: z.string().trim().min(1),
        matchType: z.enum(["NEGATIVE_PHRASE", "NEGATIVE_EXACT"]),
      }),
    )
    .max(1_000)
    .default([]),
});

const ProductTargetingSchema = z.object({
  type: z.literal("product"),
  targets: z
    .array(
      z.object({
        expressionType: z.enum(["ASIN_SAME_AS", "ASIN_CATEGORY_SAME_AS", "ASIN_BRAND_SAME_AS"]),
        value: z.string().trim().min(1),
        bid: z.number().positive(),
      }),
    )
    .min(1),
  negatives: z
    .array(
      z.object({
        expressionType: z.literal("ASIN_SAME_AS"),
        value: z.string().trim().min(1),
      }),
    )
    .default([]),
});

export const SponsoredBrandsCreationSubmitSchema = z
  .object({
    adProduct: z.literal("sb"),
    requestId: z.string().uuid(),
    campaign: z.object({
      /** Desired state after the durable hierarchy has been created and read back. */
      state: z.enum(["PAUSED", "ENABLED"]).default("ENABLED"),
      name: z.string().trim().min(1),
      portfolioId: z.null(),
      brandEntityId: z.string().min(1),
      startDate: CalendarDateSchema,
      endDate: CalendarDateSchema.nullable(),
      dailyBudget: z.number().positive(),
      bidding: z.object({
        automated: z.boolean(),
        placementAdjustments: z
          .array(
            z.object({
              placement: SponsoredBrandPlacementSchema,
              percentage: z.number().int().min(0).max(900),
            }),
          )
          .max(4)
          .default([]),
        newToBrandAdjustmentPercentage: z.number().int().min(0).max(900).nullable(),
      }),
    }),
    adGroup: z.object({ name: z.string().trim().min(1) }),
    ad: SponsoredBrandAdSchema,
    targeting: z.discriminatedUnion("type", [KeywordTargetingSchema, ProductTargetingSchema]),
  })
  .superRefine((submission, ctx) => {
    const { bidding } = submission.campaign;
    if (
      bidding.automated &&
      (bidding.placementAdjustments.length > 0 || bidding.newToBrandAdjustmentPercentage !== null)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Automated bidding cannot include custom bid adjustments",
        path: ["campaign", "bidding"],
      });
    }
    if (
      !bidding.automated &&
      bidding.placementAdjustments.length === 0 &&
      bidding.newToBrandAdjustmentPercentage === null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Manual bidding requires a custom bid adjustment",
        path: ["campaign", "bidding"],
      });
    }
    if (bidding.placementAdjustments.length > 0) {
      const placements = new Set(
        bidding.placementAdjustments.map((adjustment) => adjustment.placement),
      );
      if (placements.size !== SponsoredBrandPlacementSchema.options.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Placement bidding requires all four unique placements",
          path: ["campaign", "bidding", "placementAdjustments"],
        });
      }
    }
    if (
      submission.campaign.endDate &&
      submission.campaign.endDate < submission.campaign.startDate
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "endDate must be on or after startDate",
        path: ["campaign", "endDate"],
      });
    }
    if (
      submission.ad.format === "video" &&
      submission.ad.landingPage.type === "product_detail" &&
      submission.ad.products[0] !== submission.ad.landingPage.asin
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Video product and product-detail landing ASIN must match",
        path: ["ad", "landingPage", "asin"],
      });
    }
    if (
      submission.ad.format === "video" &&
      submission.ad.landingPage.type === "store" &&
      submission.ad.creative.brand === null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Store landing pages require brand creative for video ads",
        path: ["ad", "creative", "brand"],
      });
    }
    if (
      submission.ad.format === "product_collection" &&
      new Set(submission.ad.products).size !== submission.ad.products.length
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Product Collection creative products must be unique",
        path: ["ad", "products"],
      });
    }
    if (submission.ad.format === "store_spotlight") {
      const pageIds = submission.ad.creative.subpages.map((page) => page.pageId);
      const asins = submission.ad.creative.subpages.map((page) => page.asin);
      if (new Set(pageIds).size !== pageIds.length || new Set(asins).size !== asins.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Store Spotlight subpages and products must be unique",
          path: ["ad", "creative", "subpages"],
        });
      }
    }
  });
export type SponsoredBrandsCreationSubmit = z.infer<typeof SponsoredBrandsCreationSubmitSchema>;
